import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axios';
import { Container, Row, Col, Card, Button, ButtonGroup, Spinner, Alert, ProgressBar, Table } from 'react-bootstrap';
import AuthDebug from '../components/AuthDebug';

// Empty data defaults - dashboard builds as user adds data

const Analytics = () => {
  // Empty data with zeros instead of samples
  const EMPTY_USER_ACTIVITY = [
    {date: '2025-03-20', meetings: 0, minutes: 0, summaries: 0, tasks: 0},
    {date: '2025-03-21', meetings: 0, minutes: 0, summaries: 0, tasks: 0},
    {date: '2025-03-22', meetings: 0, minutes: 0, summaries: 0, tasks: 0},
    {date: '2025-03-23', meetings: 0, minutes: 0, summaries: 0, tasks: 0},
    {date: '2025-03-24', meetings: 0, minutes: 0, summaries: 0, tasks: 0},
    {date: '2025-03-25', meetings: 0, minutes: 0, summaries: 0, tasks: 0},
    {date: '2025-03-26', meetings: 0, minutes: 0, summaries: 0, tasks: 0}
  ];

  const EMPTY_MEETING_METRICS = {
    avgDuration: 0,
    avgSpeakers: 0,
    avgActionItems: 0,
    avgTopics: 0
  };

  const EMPTY_SUMMARY_QUALITY = [
    { name: 'Excellent', value: 0 },
    { name: 'Good', value: 0 },
    { name: 'Average', value: 0 },
    { name: 'Poor', value: 0 }
  ];

  const EMPTY_USER_ENGAGEMENT = [
    { metric: 'Avg. Time on Platform', value: '0 min/day' },
    { metric: 'Meetings per User', value: '0/week' }, 
    { metric: 'Sharing Rate', value: '0%' },
    { metric: 'Return Rate', value: '0%' }
  ];

  const [analyticsData, setAnalyticsData] = useState({
    userActivity: EMPTY_USER_ACTIVITY,
    meetingMetrics: EMPTY_MEETING_METRICS,
    summaryQuality: EMPTY_SUMMARY_QUALITY,
    userEngagement: EMPTY_USER_ENGAGEMENT
  });
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [error, setError] = useState(null);
  const [usingSampleData, setUsingSampleData] = useState(true);

  const COLORS = ['primary', 'success', 'warning', 'danger', 'info'];

  const fetchAnalyticsData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    console.log(`Fetching analytics data for timeRange: ${timeRange}`);
    
    // Get the current user and token from localStorage
    const userJson = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!token || !userJson) {
      console.warn('No authentication token or user found in localStorage');
      setError('You need to be logged in to view your analytics data');
      setIsLoading(false);
      setUsingSampleData(true);
      return; // Exit early with sample data
    }
    
    try {
      // Parse user data to get user ID for debugging
      const userData = JSON.parse(userJson);
      console.log(`Logged in as user: ${userData.email} (ID: ${userData.id})`);
      
      // Use the dashboard endpoint with explicit userId parameter
      const apiUrl = `/api/analytics/dashboard?timeRange=${timeRange}&userId=${userData.id}`;
      console.log(`Making request to: ${apiUrl}`);
      
      // Attempt to fetch analytics data with explicit headers
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('API Response received:', response.data);
      
      // Check if we got valid data
      if (!response.data) {
        console.warn('API returned empty response, using sample data');
        setUsingSampleData(true);
        return; // Keep using the existing sample data
      }
      
      // Check if we have any real data in any of the sections
      const hasUserActivity = response.data.userActivity && response.data.userActivity.length > 0;
      const hasMeetingMetrics = response.data.meetingMetrics && Object.keys(response.data.meetingMetrics).length > 0;
      const hasSummaryQuality = response.data.summaryQuality && response.data.summaryQuality.length > 0;
      const hasUserEngagement = response.data.userEngagement && response.data.userEngagement.length > 0;
      
      // If we have no real data at all, show empty state
      if (!hasUserActivity && !hasMeetingMetrics && !hasSummaryQuality && !hasUserEngagement) {
        console.log('No analytics data yet - user needs to process meetings first');
        setUsingSampleData(true);
        // Keep using empty data defaults
        return;
      }
      
      // Update with API data, keeping empty defaults for missing sections
      setAnalyticsData(prev => ({
        userActivity: hasUserActivity ? response.data.userActivity : prev.userActivity,
        meetingMetrics: hasMeetingMetrics ? response.data.meetingMetrics : prev.meetingMetrics,
        summaryQuality: hasSummaryQuality ? response.data.summaryQuality : prev.summaryQuality,
        userEngagement: hasUserEngagement ? response.data.userEngagement : prev.userEngagement
      }));
      
      // Mark if we're using at least some sample data
      setUsingSampleData(!hasUserActivity || !hasMeetingMetrics || !hasSummaryQuality || !hasUserEngagement);
      
      console.log('Analytics data updated successfully');
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError(`Error loading data: ${error.message}`);
      setUsingSampleData(true);
      
      // Log detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
      }
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    console.log('Analytics component mounted or timeRange changed');
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Helper to find the max value in user activity for a specific key
  const getMaxValue = (data, key) => {
    return Math.max(...data.map(item => item[key]));
  };

  // Calculate total of summary quality values for percentages
  const summaryQualityTotal = analyticsData.summaryQuality.reduce((acc, item) => acc + item.value, 0);

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <Container className="py-5">
            <h2>Analytics Dashboard</h2>
            <p className="text-muted">Gain insights into your meeting performance and user engagement</p>
            
            {/* Add AuthDebug component for troubleshooting */}
            <div className="mb-4">
              <AuthDebug />
            </div>
            
            <ButtonGroup className="mb-4">
              <Button 
                variant={timeRange === 'week' ? 'primary' : 'outline-primary'}
                onClick={() => setTimeRange('week')}
                disabled={isLoading}
              >
                Week
              </Button>
              <Button 
                variant={timeRange === 'month' ? 'primary' : 'outline-primary'}
                onClick={() => setTimeRange('month')}
                disabled={isLoading}
              >
                Month
              </Button>
              <Button 
                variant={timeRange === 'year' ? 'primary' : 'outline-primary'}
                onClick={() => setTimeRange('year')}
                disabled={isLoading}
              >
                Year
              </Button>
            </ButtonGroup>
          </Container>
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading analytics data...</p>
        </div>
      ) : error ? (
        <div className="text-center my-3">
          <Alert variant="danger">
            {error}
          </Alert>
        </div>
      ) : usingSampleData ? (
        <div className="text-center py-5">
          <div className="mb-4">
            <i className="bi bi-bar-chart-line" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
          </div>
          <h4 className="text-muted mb-3">No Analytics Data Yet</h4>
          <p className="text-muted mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
            Your dashboard will populate with real analytics as you process meetings and recordings. 
            Upload your first recording to get started!
          </p>
          <Button variant="primary" href="/upload">
            Upload Your First Recording
          </Button>
        </div>
      ) : (
        <>
          
          <Row className="mb-4">
            <Col md={6}>
              <Card className="shadow-sm mb-4">
                <Card.Header>
                  <h5 className="mb-0">User Activity</h5>
                </Card.Header>
                <Card.Body>
                  {/* Simple table representation of user activity */}
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Meetings</th>
                        <th>Minutes</th>
                        <th>Summaries</th>
                        <th>Tasks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.userActivity.slice(-5).map((item, index) => (
                        <tr key={index}>
                          <td>{item.date}</td>
                          <td>{item.meetings}</td>
                          <td>{item.minutes || 0}</td>
                          <td>{item.summaries}</td>
                          <td>{item.tasks || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Visual activity representation using progress bars */}
                  <div className="mt-4">
                    <h6>Recent Activity Trends</h6>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Meetings</span>
                        <span>{analyticsData.userActivity[analyticsData.userActivity.length - 1].meetings}</span>
                      </div>
                      <ProgressBar 
                        now={(analyticsData.userActivity[analyticsData.userActivity.length - 1].meetings / getMaxValue(analyticsData.userActivity, 'meetings')) * 100} 
                        variant="primary" 
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Meeting Minutes</span>
                        <span>{analyticsData.userActivity[analyticsData.userActivity.length - 1].minutes || 0}</span>
                      </div>
                      <ProgressBar 
                        now={(analyticsData.userActivity[analyticsData.userActivity.length - 1].minutes / getMaxValue(analyticsData.userActivity, 'minutes')) * 100} 
                        variant="info" 
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Summaries</span>
                        <span>{analyticsData.userActivity[analyticsData.userActivity.length - 1].summaries}</span>
                      </div>
                      <ProgressBar 
                        now={(analyticsData.userActivity[analyticsData.userActivity.length - 1].summaries / getMaxValue(analyticsData.userActivity, 'summaries')) * 100} 
                        variant="success" 
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="shadow-sm mb-4">
                <Card.Header>
                  <h5 className="mb-0">Meeting Metrics</h5>
                </Card.Header>
                <Card.Body>
                  <div className="row row-cols-2 g-4">
                    <div className="col">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-muted mb-2">Avg. Duration</h6>
                        <div className="d-flex align-items-baseline">
                          <h2 className="mb-0 me-2">{Math.round(analyticsData.meetingMetrics.avgDuration || 0)}</h2>
                          <span className="text-muted">minutes</span>
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-muted mb-2">Avg. Speakers</h6>
                        <div className="d-flex align-items-baseline">
                          <h2 className="mb-0 me-2">{Math.round(analyticsData.meetingMetrics.avgSpeakers || 0)}</h2>
                          <span className="text-muted">per meeting</span>
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-muted mb-2">Avg. Action Items</h6>
                        <div className="d-flex align-items-baseline">
                          <h2 className="mb-0 me-2">{Math.round(analyticsData.meetingMetrics.avgActionItems || 0)}</h2>
                          <span className="text-muted">per meeting</span>
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-muted mb-2">Avg. Topics</h6>
                        <div className="d-flex align-items-baseline">
                          <h2 className="mb-0 me-2">{Math.round(analyticsData.meetingMetrics.avgTopics || 0)}</h2>
                          <span className="text-muted">per meeting</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Card className="shadow-sm mb-4">
                <Card.Header>
                  <h5 className="mb-0">Summary Quality Ratings</h5>
                </Card.Header>
                <Card.Body>
                  {/* Simple quality ratings visualization */}
                  {analyticsData.summaryQuality.map((item, index) => (
                    <div key={index} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>{item.name}</span>
                        <span>{Math.round((item.value / summaryQualityTotal) * 100)}%</span>
                      </div>
                      <ProgressBar 
                        now={(item.value / summaryQualityTotal) * 100} 
                        variant={COLORS[index % COLORS.length]} 
                      />
                    </div>
                  ))}
                  
                  <div className="text-center mt-4">
                    <div className="d-inline-block px-4 py-2 border rounded-pill bg-light">
                      <span className="fw-bold">{summaryQualityTotal}</span> total ratings
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="shadow-sm mb-4">
                <Card.Header>
                  <h5 className="mb-0">User Engagement</h5>
                </Card.Header>
                <Card.Body>
                  <div>
                    {analyticsData.userEngagement.map((item, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center p-3 mb-3 border rounded">
                        <span className="text-muted">{item.metric}</span>
                        <span className="fs-4 fw-bold">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default Analytics;
